import type { PlpStoreLookup } from "@lib/utils/plp/normalizeStoresForPlp";
import { usePlpStores } from "@hooks/usePlpStores";
import { useStore } from "@store/index";
import { useFilterStore } from "@store/filterStore";
import type { FC } from "react";
import { memo, use, useEffect, useMemo } from "react";
import { CommonToolbarFilter } from "@components/commerce/product-listing/filters/toolbar/item/common/CommonToolbarFilter";
import { useRefinementList } from "react-instantsearch";
import { SizeToolbarFilter } from "./size/SizeToolbarFilter";
import { FilterTypes } from "@lib/constants/filtersConstants";
import { getVisibleFilterOptions, hasVisibleFilterOptions } from "@lib/utils/plp/filterOptions";
import { isStoresFacet, prepareStoreFacetOptions } from "@lib/utils/plp/mapStoreFacetOptions";
import { filterStoreFacetOptionsBySize } from "@lib/utils/plp/storeSizesFilter";
import { useLocale } from "next-intl";
import { PriceToolbarFilter } from "@components/commerce/product-listing/filters/toolbar/item/price/PriceToolbarFilter";
import type { RefinementListItem } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import type { IPartFilterFields } from "@contentful-api/types/contentful";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { scrollToTopAfterFilter } from "@lib/utils/scrollToTop";
import dynamic from "next/dynamic";
import { debounce } from "instantsearch.js/es/lib/utils";
import { FiltersContext } from "@components/commerce/product-listing/filters/context/FiltersContext";
import { activeSiteConfig } from "@config/site/site-config";

const ColorToolbarFilter = dynamic(() => import("./color/ColorToolbarFilter"), { ssr: false });

export interface ToolbarFilterItemProps extends Partial<IPartFilterFields> {
    items: RefinementListItem[];
    refine: (refinement: string) => void;
    initialFilters: {
        [p: string]: Partial<RefinementListItem>[];
    };
    stores?: PlpStoreLookup[];
    isLoading?: boolean;
}

const ToolbarFilterItem = memo(
    ({
        filterName,
        filterType,
        facetName,
        isToolbar,
        items,
        refine,
        initialFilters,
        stores,
    }: ToolbarFilterItemProps) => {
        const locale = useLocale();
        const { clientLocation, storeIdsForSelectedSizes } = use(FiltersContext);
        const unitSystem = activeSiteConfig.geo.imperialMetricSystem ? "imperial" : "metric";

        if (!hasVisibleFilterOptions(initialFilters?.[filterName], filterType)) {
            return <></>;
        }

        // Filter out items with 0 stock
        const filtersWithStock = initialFilters[filterName].filter((filter) =>
            items.some((item) => item.value === filter.value && item.count > 0)
        );
        const displayFilters = isStoresFacet(facetName)
            ? prepareStoreFacetOptions(
                  filterStoreFacetOptionsBySize(
                      filtersWithStock as { value: string; label: string }[],
                      storeIdsForSelectedSizes
                  ),
                  stores,
                  clientLocation,
                  { isAppendDistance: true, locale, unitSystem }
              )
            : filtersWithStock;

        switch (filterType) {
            case FilterTypes.price:
                return (
                    <PriceToolbarFilter
                        items={items}
                        initialFilters={displayFilters}
                        refine={refine}
                        filterName={filterName}
                        isToolbar={isToolbar}
                    />
                );
            case FilterTypes.color:
                return (
                    <ColorToolbarFilter
                        items={items}
                        initialFilters={displayFilters}
                        refine={refine}
                        filterName={filterName}
                        isToolbar={isToolbar}
                    />
                );
            case FilterTypes.size:
                return <SizeToolbarFilter filterName={filterName} isToolbar={isToolbar} />;
            default:
                return (
                    <CommonToolbarFilter
                        items={items}
                        initialFilters={getVisibleFilterOptions(displayFilters, filterType)}
                        refine={refine}
                        filterName={filterName}
                        isToolbar={isToolbar}
                        enableSearch={isStoresFacet(facetName)}
                    />
                );
        }
    }
);

ToolbarFilterItem.displayName = "ToolbarFilterItem";

type IToolbarFilterItemContainerProps = IPartFilterFields;

const ToolbarFilterItemContainer: FC<IToolbarFilterItemContainerProps> = ({
    filterName,
    facetName,
    filterType,
    isToolbar,
}) => {
    const locale = useLocale();
    const initialFilters = useStore((state) => state.data.products?.initialFilters);
    const { data: stores } = usePlpStores({
        locale,
        queryConfig: { enabled: isStoresFacet(facetName) },
    });
    const setLastLoadedPage = useStore((state) => state.setLastLoadedPage);

    // Dynamic filter store actions (no state subscriptions to avoid rerenders)
    const setFilterItems = useFilterStore((state) => state.setFilterItems);
    const facets = useFilterStore((state) => state.facets);

    const { items, refine } = useRefinementList({
        attribute: facetName || "na",
        operator: "or",
        sortBy: ["name"],
        limit: 100,
    });

    // Update store with current facet data whenever items or refine function changes
    useEffect(() => {
        const newState = facets;
        newState[filterName] = { items, refine };
        setFilterItems({ ...newState });
    }, [items]);

    const applyRefinement = useMemo(
        () =>
            debounce((item: string) => {
                // Analytics (fire immediately, no timer needed)
                AnalyticsClient.listFilterApplied(filterName, item, facetName);

                // UI actions — reset page/refine first, then scroll after layout collapse
                setLastLoadedPage(1);
                refine(item);
                scrollToTopAfterFilter();
            }, 0),
        [refine, setLastLoadedPage, filterName, facetName]
    );

    return (
        <ToolbarFilterItem
            isToolbar={isToolbar}
            filterType={filterType}
            filterName={filterName}
            facetName={facetName}
            items={items}
            refine={applyRefinement}
            initialFilters={initialFilters}
            stores={stores}
        />
    );
};

export { ToolbarFilterItemContainer as ToolbarFilterItem };

export default ToolbarFilterItemContainer;
